import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ShieldCheck, Check, X, Clock } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import InvitationsPanel from "../components/InvitationsPanel";

function AuthorityRow({ u, onApprove, onReject }) {
  const statusColor = {
    pending: "bg-[#C99B4B]/15 text-[#8A6A2E] border-[#C99B4B]/40",
    active: "bg-[#4A7C59]/10 text-[#4A7C59] border-[#4A7C59]/30",
    rejected: "bg-[#9E4747]/10 text-[#9E4747] border-[#9E4747]/30",
  }[u.status] || "bg-[#F8F9FA] text-[#5C6873] border-[#E2E8F0]";

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#E2E8F0] bg-white p-5 shadow-sm" data-testid={`authority-row-${u.id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-heading text-base font-semibold text-[#1A2024]" data-testid={`authority-name-${u.id}`}>{u.name}</h3>
          <Badge variant="outline" className={`rounded-full font-body text-[11px] capitalize ${statusColor}`} data-testid={`authority-status-${u.id}`}>
            {u.status}
          </Badge>
        </div>
        <div className="font-body text-sm text-[#5C6873]">{u.email}</div>
        <div className="font-body text-xs text-[#5C6873]">
          {u.organization || "No organisation provided"} · registered {new Date(u.created_at).toLocaleDateString()}
        </div>
      </div>
      {u.status === "pending" && (
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="border-[#9E4747]/40 text-[#9E4747] hover:bg-[#9E4747]/5" onClick={() => onReject(u.id)} data-testid={`authority-reject-${u.id}`}>
            <X size={14} className="mr-1" /> Reject
          </Button>
          <Button size="sm" className="bg-[#4A7C59] text-white hover:bg-[#3a6346]" onClick={() => onApprove(u.id)} data-testid={`authority-approve-${u.id}`}>
            <Check size={14} className="mr-1" /> Approve
          </Button>
        </div>
      )}
    </div>
  );
}

export default function AdminAuthoritiesPage() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/authorities?status=${tab}`);
      setItems(data);
    } catch (e) {
      toast.error("Could not load authorities");
    }
    setLoading(false);
  }, [tab]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onApprove = async (id) => {
    try {
      await api.post(`/admin/authorities/${id}/approve`);
      toast.success("Authority approved");
      refresh();
    } catch {
      toast.error("Approval failed");
    }
  };
  const onReject = async (id) => {
    try {
      await api.post(`/admin/authorities/${id}/reject`);
      toast.success("Authority rejected");
      refresh();
    } catch {
      toast.error("Rejection failed");
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-10" data-testid="admin-authorities-page">
      <p className="font-body text-xs uppercase tracking-[0.18em] text-[#6B7F82]">Admin · vetting</p>
      <div className="mt-1 flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-md bg-[#2E3A46] text-white">
          <ShieldCheck size={18} />
        </span>
        <h1 className="font-heading text-3xl font-semibold text-[#1A2024]">Authority requests</h1>
      </div>
      <p className="mt-2 max-w-2xl font-body text-sm text-[#5C6873]">
        Review professional accounts asking for access. Only approved authorities can add or edit
        unidentified-person records.
      </p>

      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList data-testid="admin-authorities-tabs">
          <TabsTrigger value="pending" data-testid="tab-pending"><Clock size={14} className="mr-1" /> Pending</TabsTrigger>
          <TabsTrigger value="active" data-testid="tab-active">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-5 space-y-3">
          {loading && <p className="font-body text-sm text-[#5C6873]" data-testid="admin-loading">Loading…</p>}
          {!loading && items.length === 0 && (
            <div className="rounded-lg border border-dashed border-[#E2E8F0] bg-[#F8F9FA] p-10 text-center font-body text-sm text-[#5C6873]" data-testid="admin-empty">
              Nothing in this list.
            </div>
          )}
          {items.map((u) => (
            <AuthorityRow key={u.id} u={u} onApprove={onApprove} onReject={onReject} />
          ))}
        </TabsContent>
      </Tabs>

      <div className="mt-14 border-t border-[#E2E8F0] pt-10">
        <InvitationsPanel />
      </div>
    </div>
  );
}
